import { useState } from "react";
import { Outlet } from "react-router-dom";
import { Alert, Box, Button } from "@mui/material";
import useResponsive from "../../hooks/useResponsive";
import { useSettingsContext } from "../../components/settings";
import Main from "./Main";
import Header from "./header";
import NavMini from "./nav/NavMini";
import NavVertical from "./nav/NavVertical";
import NavHorizontal from "./nav/NavHorizontal";
import { useAuthContext } from "src/auth/useAuthContext";
import { useLocation } from "react-router-dom";
import axios from "../../utils/axios";
import { useSnackbar } from "../../components/snackbar";
import {
  getAuthNamespace,
  getImpersonationTargetName,
  getUserImpersonationScope,
} from "../../auth/impersonation";

export default function DashboardLayout() {
  const { themeLayout } = useSettingsContext();
  const { user, logout } = useAuthContext();
  const isDesktop = useResponsive("up", "lg");
  const [open, setOpen] = useState(false);
  const [isEndingImpersonation, setIsEndingImpersonation] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const isNavHorizontal = themeLayout === "horizontal";
  const isNavMini = themeLayout === "mini";
  const handleOpen = () => {
    setOpen(true);
  };
  const location = useLocation();

  const handleClose = () => {
    setOpen(false);
  };

  const handleBackToAdmin = async () => {
    const adminAppUrl = process.env.REACT_APP_ADMIN_APP_URL;
    if (!adminAppUrl) {
      enqueueSnackbar("Admin portal URL is not configured", { variant: "error" });
      return;
    }
    if (isEndingImpersonation) return;

    setIsEndingImpersonation(true);
    try {
      const scope = getUserImpersonationScope(user);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/${getAuthNamespace(scope)}/impersonation/end`
      );
    } catch (error) {
      // Local credentials are still removed if the server session already expired.
    } finally {
      logout();
      window.location.assign(
        new URL(user?.adminReturnPath || "/dashboard/franchise/list", adminAppUrl).toString()
      );
    }
  };

  const impersonationBanner = user?.isImpersonating ? (
    <Alert
      severity="warning"
      action={(
        <Button color="inherit" size="small" disabled={isEndingImpersonation} onClick={handleBackToAdmin}>
          Back to Admin
        </Button>
      )}
      sx={{ mb: 2 }}
    >
      Viewing as Egora Admin — {getImpersonationTargetName(user)}
    </Alert>
  ) : null;

  const [isVenueOnline] = useState(user?.venue?.isOnline ?? false);

  const renderNavVertical = (
    <NavVertical
      openNav={open}
      onCloseNav={handleClose}
      isVenueOnline={isVenueOnline}
    />
  );

  const isThemePage =
    /^\/dashboard\/store\/themes\/Preview\/[^/]+$/.test(
      location.pathname
    ) ||
    /^\/dashboard\/store\/themes\/[^/]+\/edit$/.test(location.pathname);
  const isRetailThemePage =
    /^\/dashboard\/retail\/themes\/Preview\/[^/]+$/.test(location.pathname) ||
    /^\/dashboard\/retail\/themes\/[^/]+\/edit$/.test(location.pathname);

  if (isThemePage || isRetailThemePage) {
    return (
      <>
        {impersonationBanner}
        <Outlet />
      </>
    );
  }

  if (isNavHorizontal) {
    return (
      <>
        <Header onOpenNav={handleOpen} />

        {isDesktop ? <NavHorizontal /> : renderNavVertical}

        <Main>
          {impersonationBanner}
          <Outlet />
        </Main>
      </>
    );
  }

  if (isNavMini) {
    return (
      <>
        <Header onOpenNav={handleOpen} />

        <Box
          sx={{
            display: { lg: "flex" },
            minHeight: { lg: 1 },
          }}
        >
          {isDesktop ? (
            <NavMini isVenueOnline={isVenueOnline} />
          ) : (
            renderNavVertical
          )}

          <Main>
            {impersonationBanner}
            <Outlet />
          </Main>
        </Box>
      </>
    );
  }

  return (
    <>
      <Header onOpenNav={handleOpen} />

      <Box
        sx={{
          display: { lg: "flex" },
          minHeight: { lg: 1 },
        }}
      >
        {renderNavVertical}

        <Main>
          {impersonationBanner}
          <Outlet />
        </Main>
      </Box>
    </>
  );
}
